import * as constants from "./constants";

export interface ApiUser {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    address?: string;
    is_active: boolean;
    is_superuser?: boolean;
    is_suspended?: boolean;
    statut?: string;
    last_login?: string;
    decisions_count?: number;
    charge?: number;
    role: Array<{ id: number; name: string }>;
    created_at: string;
    updated_at?: string;
}

export interface Role {
    id: number;
    name: string;
    permissions?: string[];
}

export interface UserStats {
    decisions_count: number;
    nombre_traitees: number;
    charge: number;
    publiees: number;
    rejetees: number;
    en_brouillon: number;
}

export interface UserActivite {
    date: string;
    action: string;
    details?: string;
    [key: string]: any;
}

export interface UsersState {
    isLoading: boolean;
    users: ApiUser[];
    count: number;
    next: string | null;
    previous: string | null;
    errors: any;
    isSubmitting: boolean;
    submitError: any;
    roles: Role[];
    rolesLoading: boolean;
    // User detail
    userDetail: ApiUser | null;
    userDetailLoading: boolean;
    userDetailError: any;
    // User stats
    userStats: UserStats | null;
    userStatsLoading: boolean;
    // User activite
    userActivite: UserActivite[];
    userActiviteLoading: boolean;
    // User decisions
    userDecisions: any[];
    userDecisionsLoading: boolean;
}

export const initialState: UsersState = {
    isLoading: false,
    users: [],
    count: 0,
    next: null,
    previous: null,
    errors: null,
    isSubmitting: false,
    submitError: null,
    roles: [],
    rolesLoading: false,
    userDetail: null,
    userDetailLoading: false,
    userDetailError: null,
    userStats: null,
    userStatsLoading: false,
    userActivite: [],
    userActiviteLoading: false,
    userDecisions: [],
    userDecisionsLoading: false,
};

const usersReducer = (state = initialState, action: any): UsersState => {
    switch (action.type) {
        case constants.FETCH_USERS_REQUESTING:
            return { ...state, isLoading: true, errors: null };
        case constants.FETCH_USERS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                users: action.payload.results,
                count: action.payload.count,
                next: action.payload.next,
                previous: action.payload.previous,
            };
        case constants.FETCH_USERS_FAILED:
            return { ...state, isLoading: false, errors: action.error };

        case constants.CREATE_USER_REQUESTING:
        case constants.UPDATE_USER_REQUESTING:
        case constants.DELETE_USER_REQUESTING:
        case constants.SUSPEND_USER_REQUESTING:
        case constants.ACTIVATE_USER_REQUESTING:
            return { ...state, isSubmitting: true, submitError: null };

        case constants.CREATE_USER_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                users: [...state.users, action.payload],
                count: state.count + 1,
            };
        case constants.UPDATE_USER_SUCCESS:
        case constants.SUSPEND_USER_SUCCESS:
        case constants.ACTIVATE_USER_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                users: state.users.map(u => u.id === action.payload.id ? action.payload : u),
            };
        case constants.DELETE_USER_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                users: state.users.filter(u => u.id !== action.id),
                count: state.count - 1,
            };

        case constants.CREATE_USER_FAILED:
        case constants.UPDATE_USER_FAILED:
        case constants.DELETE_USER_FAILED:
        case constants.SUSPEND_USER_FAILED:
        case constants.ACTIVATE_USER_FAILED:
            return { ...state, isSubmitting: false, submitError: action.error };

        case constants.FETCH_ROLES_REQUESTING:
            return { ...state, rolesLoading: true };
        case constants.FETCH_ROLES_SUCCESS:
            return { ...state, rolesLoading: false, roles: action.payload };
        case constants.FETCH_ROLES_FAILED:
            return { ...state, rolesLoading: false };

        // Role CRUD
        case constants.CREATE_ROLE_REQUESTING:
        case constants.UPDATE_ROLE_REQUESTING:
        case constants.DELETE_ROLE_REQUESTING:
            return { ...state, isSubmitting: true, submitError: null };
        case constants.CREATE_ROLE_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                roles: [...state.roles, action.payload],
            };
        case constants.UPDATE_ROLE_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                roles: state.roles.map(r => r.id === action.payload.id ? action.payload : r),
            };
        case constants.DELETE_ROLE_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                roles: state.roles.filter(r => r.id !== action.id),
            };
        case constants.CREATE_ROLE_FAILED:
        case constants.UPDATE_ROLE_FAILED:
        case constants.DELETE_ROLE_FAILED:
            return { ...state, isSubmitting: false, submitError: action.error };

        // User detail
        case constants.FETCH_USER_DETAIL_REQUESTING:
            return { ...state, userDetailLoading: true, userDetailError: null };
        case constants.FETCH_USER_DETAIL_SUCCESS:
            return { ...state, userDetailLoading: false, userDetail: action.payload };
        case constants.FETCH_USER_DETAIL_FAILED:
            return { ...state, userDetailLoading: false, userDetailError: action.error };
        case constants.CLEAR_USER_DETAIL:
            return { ...state, userDetail: null, userDetailError: null, userStats: null, userActivite: [], userDecisions: [] };

        // User stats
        case constants.FETCH_USER_STATS_REQUESTING:
            return { ...state, userStatsLoading: true };
        case constants.FETCH_USER_STATS_SUCCESS:
            return { ...state, userStatsLoading: false, userStats: action.payload };
        case constants.FETCH_USER_STATS_FAILED:
            return { ...state, userStatsLoading: false };

        // User activite
        case constants.FETCH_USER_ACTIVITE_REQUESTING:
            return { ...state, userActiviteLoading: true };
        case constants.FETCH_USER_ACTIVITE_SUCCESS:
            return { ...state, userActiviteLoading: false, userActivite: action.payload.results || action.payload };
        case constants.FETCH_USER_ACTIVITE_FAILED:
            return { ...state, userActiviteLoading: false };

        // User decisions
        case constants.FETCH_USER_DECISIONS_REQUESTING:
            return { ...state, userDecisionsLoading: true };
        case constants.FETCH_USER_DECISIONS_SUCCESS:
            return { ...state, userDecisionsLoading: false, userDecisions: action.payload.results || action.payload };
        case constants.FETCH_USER_DECISIONS_FAILED:
            return { ...state, userDecisionsLoading: false };

        default:
            return state;
    }
};

export default usersReducer;
